<template>
  <div class="tunnel-deposit">
    <h2 class="text-h6 mb-2">{{ $t(authorizing ? 'onboarding.tunnel.step.deposit-authorization' : 'onboarding.tunnel.step.deposit') }}</h2>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>

    <!-- Devant une approbation : rien n'est payé maintenant ; la personne autorise le prélèvement à l'acceptation. -->
    <template v-if="authorizing">
      <p class="mb-3">{{ $t('onboarding.tunnel.deposit.authorization.explained', { organization, amount: formattedAmount }) }}</p>
      <ul class="text-body-2 mb-4 tunnel-deposit__facts">
        <li>{{ $t('onboarding.tunnel.deposit.authorization.when', { organization, paymentMethod }) }}</li>
        <li>{{ $t('onboarding.tunnel.deposit.authorization.if-refused', { organization }) }}</li>
        <li>{{ $t('onboarding.tunnel.deposit.authorization.if-failed') }}</li>
      </ul>
      <v-checkbox v-model="consent" dense hide-details class="mb-4" :label="$t('onboarding.tunnel.deposit.authorization.consent', { organization, amount: formattedAmount, paymentMethod })" />

      <div class="d-flex mt-4">
        <v-spacer />
        <v-btn small color="primary" :loading="working" :disabled="!consent" @click="authorize">
          {{ $t('onboarding.tunnel.deposit.authorization.submit') }}
        </v-btn>
      </div>
    </template>

    <!-- SEPA parti : on attend la banque, il n'y a rien d'autre à faire. -->
    <div v-else-if="deposit.status === 'processing'" class="text-center py-4">
      <v-icon size="64" color="primary" class="mb-3">mdi-bank-transfer</v-icon>
      <p>{{ $t('onboarding.tunnel.deposit.processing', { amount: formattedAmount }) }}</p>
    </div>

    <template v-else>
      <!-- Prélevé sans elle à l'acceptation, sa banque demande qu'elle le confirme : c'est pour ça qu'elle revient. -->
      <v-alert v-if="deposit.status === 'requires-action'" type="info" text dense class="mb-3">
        {{ $t('onboarding.tunnel.deposit.confirm-needed', { organization, amount: formattedAmount }) }}
      </v-alert>
      <p class="mb-2">{{ $t('onboarding.tunnel.deposit.explained', { organization, amount: formattedAmount }) }}</p>
      <p class="text-caption text--secondary mb-4">{{ $t(`onboarding.tunnel.deposit.notice.${deposit.instrument || 'card'}`) }}</p>

      <div class="d-flex mt-4">
        <v-spacer />
        <v-btn small color="primary" :loading="working" @click="pay">
          {{ $t('onboarding.tunnel.deposit.pay', { amount: formattedAmount }) }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import RegistrationRepository from '@repository/RegistrationRepository'
import StripeService from '@service/StripeService'

/**
 * La porte « acompte » : le montant réglé pour le dossier, prélevé sur le moyen de paiement que la
 * personne vient de donner, quand elle clique.
 *
 * Devant une approbation, elle ne paie pas : elle **autorise** le prélèvement dès que son inscription
 * sera acceptée, et elle a fini. Il part alors sans elle ; elle ne revient que si sa banque demande
 * de le confirmer, ou s'il est refusé. Rien n'est envoyé au serveur que le clic : le
 * montant et le moyen de paiement sont au dossier.
 *  - carte : payé tout de suite, ou après l'authentification que la banque peut demander ;
 *  - SEPA : « en cours » un ou deux jours, jusqu'à ce que la banque réponde.
 * Un refus renvoie au moyen de paiement, avec le motif (c'est le parent qui change d'étape).
 */
export default {
  name: 'TunnelDeposit',

  props: {
    token: { type: String, required: true },
    organization: { type: String, required: true },
    deposit: { type: Object, required: true },
    /** { awaited, authorizedAt } quand une approbation précède l'acompte ; null sinon */
    authorization: { type: Object, default: null },
    /** Le moyen de paiement donné, en mots */
    paymentMethod: { type: String, default: '' },
  },

  data() {
    return { working: false, error: null, consent: false }
  },

  computed: {
    authorizing() {
      return !!this.authorization?.awaited
    },

    formattedAmount() {
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: (this.deposit.currency || 'eur').toUpperCase() })
        .format(this.deposit.amount || 0)
    },
  },

  methods: {
    async authorize() {
      this.working = true
      this.error = null
      try {
        this.$emit('updated', await RegistrationRepository.authorizeDeposit(this.token))
      } catch (e) {
        const data = e?.response?.data
        const key = data?.messageKey
        this.error = key && this.$te(key) ? this.$t(key, data.parameters || {}) : this.$t('error-occurred')
      } finally {
        this.working = false
      }
    },

    async pay() {
      this.working = true
      this.error = null
      try {
        let view = await RegistrationRepository.payDeposit(this.token)
        if (view.depositClientSecret) {
          // La banque demande une authentification : Stripe l'affiche, puis le serveur relit l'issue.
          const stripe = await StripeService.initStripe()
          const { error } = await stripe.handleNextAction({ clientSecret: view.depositClientSecret })
          if (error) {
            this.error = error.message
          }
          view = await RegistrationRepository.confirmDeposit(this.token)
        }
        this.$emit('updated', view)
      } catch (e) {
        const data = e?.response?.data
        const key = data?.messageKey
        this.error = key && this.$te(key) ? this.$t(key, data.parameters || {}) : this.$t('error-occurred')
      } finally {
        this.working = false
      }
    },
  },
}
</script>

<style scoped>
.tunnel-deposit__facts {
  padding-left: 18px;
}
.tunnel-deposit__facts li {
  margin-bottom: 4px;
}
</style>
